import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  CircleNotch,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { CategoryPicker } from '@/components/activities/CategoryPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import {
  createActivity,
  updateActivity,
  type Activity,
  type ActivityType,
} from '@/lib/activitiesApi';
import { fetchMatters } from '@/lib/mattersApi';
import { sanitizeMoneyInput, parseMoney, formatMoney2 } from '@/lib/money';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  /** Pin the activity to a specific matter (used when opened from CaseDetailPage). */
  matterId?: string;
  /** Pass an existing activity to edit; omit to create. */
  initial?: Activity;
  /** Pre-select the entry type when creating. */
  defaultType?: ActivityType;
  /**
   * Open the entry to READ, not to change.
   *
   * An entry that has been billed, or that someone else logged, cannot be edited — and editing
   * was the only way to open one at all, so the notes and the hourly rate behind a billed item
   * could not be seen anywhere once it reached a fee note. That is precisely the entry a
   * barrister is asked about when a client queries the bill.
   */
  readOnly?: boolean;
  onClose: () => void;
  onSaved: (a: Activity) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function minutesFromInput(raw: string): number | null {
  // Accepts "1.5", "1.5h", "1h 30m", "90m", "1:30"
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return Math.round(parseFloat(trimmed) * 60);
  }
  if (/^\d+(\.\d+)?h$/.test(trimmed)) {
    return Math.round(parseFloat(trimmed) * 60);
  }
  if (/^\d+m$/.test(trimmed)) {
    return parseInt(trimmed.replace('m', ''), 10);
  }
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    return parseInt(colonMatch[1]!, 10) * 60 + parseInt(colonMatch[2]!, 10);
  }
  const hmMatch = trimmed.match(/^(\d+)h\s*(\d+)m$/);
  if (hmMatch) {
    return parseInt(hmMatch[1]!, 10) * 60 + parseInt(hmMatch[2]!, 10);
  }
  return null;
}

export function ActivityModal({
  matterId,
  initial,
  defaultType = 'time',
  readOnly = false,
  onClose,
  onSaved,
}: Props) {
  const qc = useQueryClient();
  const isEdit = Boolean(initial);

  // The caller always chooses the type — every open site passes `defaultType`, and the type
  // cannot change after create (the API rejects it, to protect the duration/quantity
  // invariant). So this is fixed for the life of the dialog, not state.
  const entryType: ActivityType = initial?.entryType ?? defaultType;
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(
    initial?.caseId ?? matterId ?? null,
  );
  const [category, setCategory] = useState(initial?.category ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [activityDate, setActivityDate] = useState(
    initial ? initial.activityDate.slice(0, 10) : todayIso(),
  );
  const [durationInput, setDurationInput] = useState(
    initial?.durationMinutes
      ? `${(initial.durationMinutes / 60).toFixed(2).replace(/\.?0+$/, '')}h`
      : '',
  );
  const [rate, setRate] = useState(initial?.rate ?? '');
  const [quantity, setQuantity] = useState(initial?.quantity ?? '1');
  const [amount, setAmount] = useState(initial?.amount ?? '');
  const [isBillable, setIsBillable] = useState(initial?.isBillable ?? true);
  const [notes, setNotes] = useState(initial?.notes ?? '');

  const mattersQ = useQuery({
    queryKey: ['matters-picker-activity'],
    queryFn: () => fetchMatters().then((r) => r.matters),
    enabled: !matterId,
    staleTime: 0,
  });

  // Pre-fill the rate from a matter's lead barrister default — out of scope
  // for v1 (no matter-rate model yet); planned for the matter dashboard sprint.
  useEffect(() => {
    // intentionally empty — placeholder so the lint rule about effects with no
    // body doesn't complain when we wire matter defaults later.
  }, [selectedMatterId]);

  const minutes = useMemo(() => minutesFromInput(durationInput), [durationInput]);

  const computedTimeAmount = useMemo(() => {
    if (entryType !== 'time') return null;
    const r = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (!minutes || !r || isNaN(r)) return null;
    return ((minutes / 60) * r).toFixed(2);
  }, [entryType, minutes, rate]);

  // What the form must have before it can be saved. This MIRRORS the checks inside mutationFn
  // below — they stay as the backstop, but a required field should be visible on the form, not
  // discovered by pressing Save and reading a toast. Every field marked * here carries one.
  const parsedAmount = parseMoney(typeof amount === 'string' ? amount : String(amount));
  const missingRequired =
    !description.trim() ||
    !category.trim() ||
    !activityDate ||
    (entryType === 'time'
      ? !minutes
      : !parsedAmount || isNaN(parsedAmount) || parsedAmount <= 0);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!description.trim()) throw new Error('Title is required');
      if (entryType === 'time') {
        if (!minutes) throw new Error('Duration must be a positive value (e.g. 1.5h, 90m)');
        const r = typeof rate === 'string' ? parseFloat(rate) : rate;
        if (rate && (isNaN(r as number) || (r as number) < 0)) throw new Error('Invalid rate');

        if (isEdit && initial) {
          return updateActivity(initial.id, {
            caseId: selectedMatterId,
            category: category.trim() || null,
            description: description.trim(),
            activityDate,
            durationMinutes: minutes,
            rate: r ? (r as number) : undefined,
            isBillable,
            notes: notes.trim() || null,
          });
        }
        return createActivity({
          entryType: 'time',
          caseId: selectedMatterId,
          category: category.trim() || null,
          description: description.trim(),
          activityDate,
          durationMinutes: minutes,
          rate: r ? (r as number) : undefined,
          isBillable,
          notes: notes.trim() || null,
        });
      } else {
        const a = parseMoney(typeof amount === 'string' ? amount : String(amount));
        if (!a || isNaN(a as number) || (a as number) <= 0) {
          throw new Error('Amount must be greater than 0');
        }
        const q = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
        const qty = q && !isNaN(q as number) ? (q as number) : 1;

        if (isEdit && initial) {
          return updateActivity(initial.id, {
            caseId: selectedMatterId,
            category: category.trim() || null,
            description: description.trim(),
            activityDate,
            quantity: qty,
            amount: a as number,
            isBillable,
            notes: notes.trim() || null,
          });
        }
        return createActivity({
          entryType: 'expense',
          caseId: selectedMatterId,
          category: category.trim() || null,
          description: description.trim(),
          activityDate,
          quantity: qty,
          amount: a as number,
          isBillable,
          notes: notes.trim() || null,
        });
      }
    },
    onSuccess: (a) => {
      toast.success(isEdit ? 'Activity updated' : 'Activity logged');
      qc.invalidateQueries({ queryKey: ['activities'] });
      qc.invalidateQueries({ queryKey: ['activity-totals'] });
      // The category list is DERIVED from saved activities, so a new category only exists once
      // this save lands. Without this the picker served its cached list and the category the
      // user had just created was missing from it — for a full minute, with no explanation.
      qc.invalidateQueries({ queryKey: ['activity-categories'] });
      onSaved(a);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Could not save the activity'));
    },
  });

  // Every field is locked while a save is in flight, and permanently in read-only mode. The
  // dialog's own close control stays live in both cases — a reader must always be able to leave.
  const locked = readOnly || saveMut.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saveMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold tracking-tight">
              {readOnly
                ? entryType === 'time'
                  ? 'Time entry'
                  : 'Expense'
                : isEdit
                  ? 'Edit activity'
                  : entryType === 'time'
                    ? 'New time entry'
                    : 'New expense'}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {readOnly
                ? // Say WHY it cannot be changed. "Read only" on its own reads as a fault; a
                  // billed entry is settled on purpose, and another person's entry is theirs.
                  initial?.invoiceStatus === 'billed'
                  ? 'Already billed, so this entry can no longer be changed.'
                  : 'This entry can be viewed but not changed.'
                : entryType === 'time'
                  ? 'Log billable or non-billable time spent on a matter.'
                  : 'Log a disbursement or expense.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saveMut.isPending && onClose()}
            disabled={saveMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">

          {/* Matter selector */}
          {!matterId && (
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Matter <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
              </label>
              <select
                value={selectedMatterId ?? ''}
                onChange={(e) => setSelectedMatterId(e.target.value || null)}
                disabled={locked}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600"
              >
                <option value="">— Internal time (no matter) —</option>
                {mattersQ.data?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.number} · {m.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title — the API field is still `description`; this is the on-screen label only. */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                entryType === 'time'
                  ? 'e.g. Drafting written submissions'
                  : 'e.g. Train ticket to court'
              }
              maxLength={2000}
              disabled={locked}
            />
          </div>

          {/* Date + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Date <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={activityDate}
                onChange={(e) => setActivityDate(e.target.value)}
                disabled={locked}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Category <span className="text-red-500">*</span>
              </label>
              <CategoryPicker
                entryType={entryType}
                value={category}
                onChange={setCategory}
                disabled={locked}
              />
            </div>
          </div>

          {/* Type-specific fields */}
          {entryType === 'time' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Duration <span className="text-red-500">*</span>
                </label>
                <Input
                  value={durationInput}
                  onChange={(e) => setDurationInput(e.target.value)}
                  placeholder="1.5h, 90m, 1:30"
                  disabled={locked}
                />
                {durationInput && !minutes && (
                  <div className="mt-1 text-[10px] text-red-600 dark:text-red-400">Could not parse duration</div>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Hourly rate (£)
                </label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder="250.00"
                  disabled={locked}
                />
              </div>
              {computedTimeAmount && (
                <div className="col-span-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                  Will bill <strong>£{computedTimeAmount}</strong> ({(minutes! / 60).toFixed(2)} h × £{rate})
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Quantity
                </label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.001"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="1"
                  disabled={locked}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Amount (£) <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(sanitizeMoneyInput(e.target.value))}
                  onBlur={() => setAmount(formatMoney2(amount))}
                  placeholder="45.00"
                  disabled={locked}
                />
              </div>
            </div>
          )}

          {/* Billable toggle */}
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
            <input
              type="checkbox"
              checked={isBillable}
              onChange={(e) => setIsBillable(e.target.checked)}
              disabled={locked}
              className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
            />
            <span className="font-medium">Billable</span>
            <span className="text-muted-foreground">
              {isBillable ? 'will be added to the invoice' : 'firm-cost / pro-bono — not billed'}
            </span>
          </label>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Notes <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
            </label>
            <ExpandableTextarea
              label="Notes"
              value={notes}
              onChange={setNotes}
              placeholder="Internal notes — not shown on the invoice."
              rows={2}
              maxLength={2000}
              disabled={locked}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          {readOnly ? (
            // Nothing to save, so nothing to cancel — one honest way out.
            <Button type="button" size="sm" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={locked}>
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => saveMut.mutate()}
                disabled={locked || missingRequired}
                className="gap-1.5"
              >
                {saveMut.isPending && <CircleNotch size={14} className="animate-spin" />}
                {isEdit ? 'Save changes' : entryType === 'time' ? 'Log time' : 'Log expense'}
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
